import { inferRouteObject } from 'react-router-typed-object';
import { z } from 'zod';

import { IntegrationType } from '@/services/ws-header-tag/src/main/resources/rest-api';
import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, or } from '@/js/libs/use-auth';

import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import HeaderTag, {
  BidReductionTab,
  CompanyFloorRuleFormSearchKey,
  PlacementDetailsTab,
  PlacementSearchKey,
  TagDetailsTab,
  TagFormSearchKey,
  TagsListSearchKey,
} from 'src/scenes/HeaderTag/index.ts';
import { PlacementsHistorySearchParams } from 'src/scenes/HeaderTag/pages/TagDetails/containers/PlacementsHistory/types.ts';
import Status from 'src/scenes/Status/index.ts';

const integrationTypeValues = new Set<string>(Object.values(IntegrationType));
const validateIntegrationType = (params: { integrationType?: string }) => integrationTypeValues.has(params.integrationType ?? '');

const index = true;

export const headerTagRouteObject = inferRouteObject({
  path: 'header-tag',
  element: <PrivateRoute roles={or(Roles.ADMIN, Roles.HEADER_TAG)} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: HeaderTag.Helmet,
      children: [
        {
          index,
          element: <Navigate to={`./tags/${IntegrationType.CLIENT}`} replace />,
        },
        {
          path: 'tags',
          children: [
            { index, element: <Navigate to={`./${IntegrationType.CLIENT}`} replace /> },
            {
              path: ':integrationType',
              element: <RouteParamsValidator validate={validateIntegrationType} invalidStateElement={<Status.NotFound />} />,
              searchParams: z.object({
                [TagsListSearchKey.SEARCH_QUERY]: z.string().optional(),
              }).parse,
              children: [
                { index, Component: HeaderTag.TagsList },
                {
                  path: 'create',
                  Component: HeaderTag.TagForm,
                },
                {
                  path: 'edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    {
                      path: ':headerTagId',
                      Component: HeaderTag.TagForm,
                      searchParams: z.object({
                        [TagFormSearchKey.TAG_DETAILS_TAB]: z.string().optional(),
                      }).parse,
                    },
                  ],
                },
                {
                  path: ':headerTagId',
                  children: [
                    { index, element: <Navigate to='./placements' replace /> },
                    {
                      Component: HeaderTag.TagDetails.Layout,
                      children: [
                        { path: TagDetailsTab.placements, Component: HeaderTag.TagDetails.PlacementsList },
                        { path: TagDetailsTab.changelog, Component: HeaderTag.TagDetails.Changelog },
                        { path: TagDetailsTab.rawScripts, Component: HeaderTag.TagDetails.RawScriptsList },
                        { path: TagDetailsTab.disableRules, Component: HeaderTag.TagDetails.DisableRuleList },
                        { path: TagDetailsTab.dynamicPlacements, Component: HeaderTag.TagDetails.DynamicPlacementsList },
                        {
                          path: TagDetailsTab.floorRule,
                          children: [{ index, Component: HeaderTag.TagDetails.FloorRule }],
                        },
                        { path: TagDetailsTab.floorExperiments, Component: HeaderTag.TagDetails.FloorExperiments },
                        {
                          path: TagDetailsTab.live,
                          Component: HeaderTag.TagDetails.RealtimeAnalytics,
                        },
                        { path: TagDetailsTab.logbook, Component: HeaderTag.TagDetails.Logbook },
                      ],
                    },
                    {
                      path: TagDetailsTab.floorRule,
                      children: [
                        { path: 'create', Component: HeaderTag.FloorRuleForm },
                        {
                          path: 'edit',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: ':floorRuleId', Component: HeaderTag.FloorRuleForm },
                          ],
                        },
                        {
                          path: ':floorRuleId',
                          children: [
                            { index, element: <Navigate to='../' replace /> },
                            { path: 'history', Component: HeaderTag.TagDetails.FloorRuleHistory },
                          ],
                        },
                      ],
                    },
                    {
                      path: TagDetailsTab.floorExperiments,
                      children: [
                        { path: 'create', Component: HeaderTag.FloorExperimentForm },
                        { path: ':floorExperimentId', Component: HeaderTag.FloorExperimentDetails },
                      ],
                    },
                    {
                      path: TagDetailsTab.dynamicPlacements,
                      children: [{ path: 'history', Component: HeaderTag.TagDetails.DynamicPlacementHistory }],
                    },
                    {
                      path: TagDetailsTab.placements,
                      children: [
                        {
                          path: 'history',
                          Component: HeaderTag.TagDetails.PlacementsHistory,
                          searchParams: PlacementsHistorySearchParams.parse,
                        },
                      ],
                    },
                    { path: 'bidders-assign', Component: HeaderTag.TagDetails.BiddersAssign },
                    {
                      path: 'placement',
                      children: [
                        {
                          index,
                          Component: HeaderTag.TagDetails.Placement,
                        },
                        {
                          path: ':placementId',
                          children: [
                            {
                              index,
                              element: <Navigate to={`./${PlacementDetailsTab.settings}`} replace />,
                            },
                            {
                              Component: HeaderTag.TagDetails.PlacementLayout,
                              children: [
                                { path: PlacementDetailsTab.settings, Component: HeaderTag.TagDetails.Placement },
                                { path: PlacementDetailsTab.live, Component: HeaderTag.TagDetails.PlacementLive },
                              ],
                            },
                          ],
                        },
                      ],
                      searchParams: z.object({
                        [PlacementSearchKey.CLONE_ID]: z.string().optional(),
                      }).parse,
                    },
                  ],
                },
              ],
            },
          ],
        },
        { path: 'raw-scripts', Component: HeaderTag.RawScriptsList },
        {
          path: 'floor-rules',
          children: [
            { index, Component: HeaderTag.FloorRulesList },
            {
              path: 'create',
              Component: HeaderTag.FloorRuleForm,
              searchParams: z.object({
                [CompanyFloorRuleFormSearchKey.HEADER_TAG_ID]: z.string().optional(),
              }).parse,
            },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                {
                  path: ':floorRuleId',
                  Component: HeaderTag.FloorRuleForm,
                  searchParams: z.object({
                    [CompanyFloorRuleFormSearchKey.HEADER_TAG_ID]: z.string().optional(),
                  }).parse,
                },
              ],
            },
          ],
        },
        {
          path: 'bidders-configs',
          children: [
            { index, Component: HeaderTag.BiddersConfigsList },
            { path: 'create', Component: HeaderTag.BidderConfigForm },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':bidderId', Component: HeaderTag.BidderConfigForm },
              ],
            },
          ],
        },
        {
          path: 'bid-reduction',
          children: [
            { index, element: <Navigate to={`./${BidReductionTab.modelDashboard}`} replace /> },
            { path: BidReductionTab.modelDashboard, Component: HeaderTag.BidReduction.ModelDashboard },
            {
              path: BidReductionTab.configs,
              children: [
                { index, Component: HeaderTag.BidReduction.BidReductionConfigsList },
                { path: 'create', Component: HeaderTag.BidReduction.BidReductionConfigForm },
                {
                  path: 'edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    { path: ':configId', Component: HeaderTag.BidReduction.BidReductionConfigForm },
                  ],
                },
              ],
            },
          ],
        },
        { path: '*', element: <Navigate to='/header-tag/tags/CLIENT' replace /> },
      ],
    },
  ],
});
